import React, { useEffect, useState } from "react";
import { api, apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { PageTitle, SectionCard } from "@/components/Bits";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronDown, ChevronRight, Coins } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const periodLabel = (p) => { const [y, m] = (p || "").split("-"); return m ? `${MONTHS[+m - 1]} ${y}` : p; };

export default function MyCommissions() {
  const [entries, setEntries] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState({});

  useEffect(() => {
    api.get("/commissions/mine")
      .then((r) => setEntries(r.data.entries || []))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoaded(true));
  }, []);

  const ytd = entries.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="animate-fade-up">
      <PageTitle overline="My Earnings" title="My Commissions" />

      {loaded && entries.length === 0 ? (
        <SectionCard title="Commissions">
          <div className="text-center py-12 text-muted-foreground" data-testid="mycomm-empty">
            <Coins className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>No commissions recorded yet.</p>
          </div>
        </SectionCard>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-3 rounded-md border border-border bg-card px-4 py-3" data-testid="mycomm-total">
            <Coins className="h-5 w-5 text-primary" />
            <span className="text-sm text-muted-foreground">Total commissions</span>
            <span className="ml-auto font-heading text-xl font-bold">{fmtMoney(ytd)}</span>
          </div>

          <div className="space-y-3">
            {entries.map((e) => {
              const isOpen = open[e.id];
              const deals = e.deals || [];
              return (
                <div key={e.id} className="rounded-md border border-border bg-card overflow-hidden" data-testid={`mycomm-entry-${e.period}`}>
                  <button
                    onClick={() => setOpen((o) => ({ ...o, [e.id]: !o[e.id] }))}
                    className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-accent/40 transition-colors"
                    data-testid={`mycomm-toggle-${e.period}`}
                  >
                    {deals.length ? (isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : <span className="w-4" />}
                    <div className="flex-1">
                      <p className="font-medium">{periodLabel(e.period)}</p>
                      <p className="text-xs text-muted-foreground">
                        {e.deal_count || 0} deal{(e.deal_count || 0) === 1 ? "" : "s"} · Sales {fmtMoney(e.sales_value || 0)}
                        {e.source === "crm" ? " · from CRM" : " · entered by HR"}
                        {e.bonus > 0 ? ` · Bonus ${fmtMoney(e.bonus)}` : ""}
                      </p>
                    </div>
                    <span className="font-heading text-lg font-bold">{fmtMoney(e.amount || 0)}</span>
                  </button>

                  {isOpen && deals.length > 0 && (
                    <div className="border-t border-border px-4 py-2">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Deal</TableHead>
                            <TableHead>Completed</TableHead>
                            <TableHead className="text-right">Value</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {deals.map((d, i) => (
                            <TableRow key={i} data-testid={`mycomm-deal-${e.period}-${i}`}>
                              <TableCell>{d.title || "Deal"}</TableCell>
                              <TableCell>{d.completed_date ? fmtDate(d.completed_date) : "—"}</TableCell>
                              <TableCell className="text-right font-mono-num">{fmtMoney(d.value || 0)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
